import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { GrooveSurfaceDirective } from '@groove/angular';
import { CATEGORY_LABELS } from '../../../blog/blog-categories';
import { BLOG_POSTS } from '../../../blog/blog-posts-token';
import type { BlogCategory, BlogPost } from '../../../blog/blog-types';
import { PageCtaComponent } from '../../../content/page-cta/page-cta.component';
import { SectionHeadComponent } from '../../../content/section-head/section-head.component';
import { SEOService } from '../../../seo.service';
import { buildBlogIndexPageSchemas } from './blog-index-jsonld';

interface LibraryGroup {
  readonly category: BlogCategory;
  readonly label: string;
  readonly description: string;
  readonly posts: readonly BlogPost[];
  readonly totalCount: number;
}

function byDateDesc(a: BlogPost, b: BlogPost): number {
  return b.datePublished.localeCompare(a.datePublished);
}

// The library reads as one column, one section per category, ordered by what
// a prospective student cares about — NOT CATEGORIES' declared order (which
// puts Practice & Mindset after Repertoire & Reading) and not alphabetically.
// gear-and-setup has zero posts today and choosing-lessons is the front-door
// tier above, so neither belongs here; both are simply absent from this list
// rather than filtered out at runtime.
type LibraryCategorySlug = Extract<
  BlogCategory,
  | 'technique'
  | 'theory-and-harmony'
  | 'practice-and-mindset'
  | 'repertoire-and-reading'
>;

const LIBRARY_CATEGORY_ORDER: readonly LibraryCategorySlug[] = [
  'technique',
  'theory-and-harmony',
  'practice-and-mindset',
  'repertoire-and-reading',
];

// A prospective student reads these before a category heading means anything
// to them — written for that reader, not a teaching-library visitor. Keyed by
// the same four slugs as LIBRARY_CATEGORY_ORDER.
const LIBRARY_CATEGORY_DESCRIPTIONS: Readonly<
  Record<LibraryCategorySlug, string>
> = {
  technique:
    'How your hands work on the instrument, right hand and left, from the basics of form to slap, tapping and playing with a pick.',
  'theory-and-harmony':
    'Triads, scales, modes and chord sounds, and how to organize the notes on the fingerboard so your bass lines make sense over the chords.',
  'practice-and-mindset':
    'Groove, ear training, and how to practice the way you are actually going to play.',
  'repertoire-and-reading':
    'Building bass lines and walking lines, reading notation and rhythm, and the raw material I hand my students to work from.',
};

// The list under each category heading is capped at five — the rest live
// behind "All N <category> posts →" (the /blog/topic/<category> page).
const LIBRARY_LIST_LIMIT = 5;

/**
 * The two-tier blog index. The blog's real job is getting a prospective
 * student in the door; the teaching library is a second, demoted job. This
 * page renders that split directly: a pinned "Thinking about lessons?" block
 * of front-door question cards above a visually demoted library grouped by
 * category (smaller type, denser list, no hero images).
 *
 * FRONT-DOOR SELECTION RULE: every post whose `type` is `front-door` —
 * derived by scripts/build-blog.mts from the `choosing-lessons` category
 * unless the front matter says otherwise. That set IS the posts written to
 * answer a prospective student's questions before they book (cost, online
 * vs. in-person, how long it takes, whether it's worth it, kids vs. adults,
 * self-taught vs. lessons, too old to learn, which bass to start on).
 */
@Component({
  selector: 'app-blog-index',
  imports: [
    RouterLink,
    GrooveSurfaceDirective,
    SectionHeadComponent,
    PageCtaComponent,
  ],
  templateUrl: './blog-index.component.html',
  styleUrl: './blog-index.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogIndexComponent implements OnDestroy {
  private readonly allPosts = inject(BLOG_POSTS);
  private seo = inject(SEOService);

  readonly hasPosts = computed(() => this.allPosts.length > 0);

  protected readonly frontDoorPosts = computed<readonly BlogPost[]>(() =>
    [...this.allPosts]
      .filter((post) => post.type === 'front-door')
      .sort(byDateDesc),
  );

  // One section per category, in LIBRARY_CATEGORY_ORDER (not CATEGORIES'
  // declared order, not alphabetical), skipping any category with no posts.
  // Posts stay in the order BLOG_POSTS already carries — build-blog.mts sorts
  // POSTS newest-first once at generation time, and BlogTopicComponent's own
  // `posts` computed re-sorts nothing, so filtering without re-sorting here is
  // what keeps this list and "All N … posts →"'s target page in the same
  // order. The front-door posts above already cover that job, so that tier
  // doesn't repeat here — a post appears once, in whichever tier is its job.
  protected readonly libraryGroups = computed<readonly LibraryGroup[]>(() => {
    const posts = this.allPosts;
    return LIBRARY_CATEGORY_ORDER.map((slug) => {
      const categoryPosts = posts.filter((p) => p.category === slug);
      return {
        category: slug,
        label: CATEGORY_LABELS[slug] ?? slug,
        description: LIBRARY_CATEGORY_DESCRIPTIONS[slug],
        posts: categoryPosts.slice(0, LIBRARY_LIST_LIMIT),
        totalCount: categoryPosts.length,
      };
    }).filter((group) => group.totalCount > 0);
  });

  constructor() {
    // No route param to react to, so a plain constructor call is enough —
    // this page's JSON-LD never changes after mount. Sets the Blog schema
    // that used to live in app.routes.ts's static data.seo.jsonLd; see
    // blog-index-jsonld.ts for why this moved.
    this.seo.setRouteSchemas(buildBlogIndexPageSchemas());
  }

  ngOnDestroy(): void {
    this.seo.clearRouteSchemas();
  }
}
